/**
 * Route guard for a module.
 *
 * Blocks direct route access — deep links, bookmarks and browser history all go
 * through here — and renders NOTHING for the module when access is absent: no
 * skeleton, no empty state, no "you don't have permission to see Customers".
 * A placeholder naming the module would confirm the module exists, which is
 * exactly what hiding it is supposed to avoid.
 *
 * The children are not mounted, so no query fires and no data is prefetched.
 *
 * None of this is protection. The backend refuses the same request regardless of
 * what the browser decides to render.
 */
import type { JSX, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import type { AccessLevel, ActionKey, ModuleKey } from "@yazamco/shared-types";
import { useAuth } from "./AuthContext";
import { useAccess } from "./useAccess";

interface RequireModuleProps {
  module?: ModuleKey;
  /**
   * For a screen that composes several modules (the access administration page
   * shows users AND roles): any one of these is enough to open it. Each section
   * inside still checks its own module, and the API checks every request.
   */
  anyOf?: ModuleKey[];
  /**
   * Any one of these ACTIONS also opens the route, whatever the module levels
   * say. For a screen that hosts a surface belonging to another module — see
   * `anyAction` in `components/layout/nav.ts`, which this mirrors so a deep
   * link and the sidebar cannot disagree about what is reachable.
   */
  anyAction?: ActionKey[];
  /** Minimum level. Defaults to `view` — enough to see the module at all. */
  level?: AccessLevel;
  children: ReactNode;
}

export function RequireModule({
  module,
  anyOf,
  anyAction,
  level = "view",
  children,
}: RequireModuleProps): JSX.Element | null {
  const { isLoading } = useAuth();
  const { atLeast, can } = useAccess();

  // Render nothing until access is known. Mounting the children first and
  // unmounting them would fire their queries.
  if (isLoading) return null;

  const modules = anyOf ?? (module ? [module] : []);
  const permitted =
    modules.some((m) => atLeast(m, level)) ||
    (anyAction ?? []).some((a) => can(a));
  if (!permitted) return <Navigate to="/forbidden" replace />;
  return <>{children}</>;
}
